"use client"

/*
 * ============================================================================
 *  CART.JS — Page du panier (affichage, modification, suppression)
 * ============================================================================
 *
 *  CE FICHIER GÈRE :
 *  1. L'affichage de la liste des produits ajoutés au panier
 *  2. La modification de la quantité d'un produit (boutons +/- ou saisie manuelle)
 *  3. La sélection multiple pour supprimer plusieurs produits d'un coup
 *  4. Le résumé financier de la commande (Montant HT, TVA, Total TTC)
 *  5. La pagination (4 produits par page)
 *  6. Le lien vers la page de validation / choix de livraison
 *
 *  PROPS REÇUES :
 *  - onSuccess : fonction appelée après chaque modification pour mettre à jour
 *                la jauge/compteur dans la barre du haut (TopBar)
 *  - param     : objet contenant les données utilisateur (optionnel)
 * ============================================================================
 */

// ── Imports ──────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react"
import AppMenu from "../../Mescomposants/AppMenu"            // Menu latéral de navigation
import { toast } from "react-toastify"                       // Notifications "toast" (succès, erreur)
import { useTheme } from "../../contexts/ThemeContext"        // Thème clair/sombre (non utilisé visuellement ici mais disponible)
import { NavLink } from "react-router-dom"                   // Liens de navigation sans rechargement de page
import { crudData, defaultImage, fullUrl } from "../../services/apiService"  // Fonctions pour appeler les API backend
import { Spinner } from "react-bootstrap"                    // Composant spinner (chargement)


/* ============================================================================
 *  STYLES CSS — Injectés dynamiquement dans le <head> du document
 * ============================================================================
 *  On crée une feuille de style en JavaScript pour les animations et effets
 *  qui ne sont pas facilement réalisables en inline style React.
 */
const styleSheet = document.createElement("style")
styleSheet.type = "text/css"
styleSheet.innerText = `
    /* Animation d'apparition : les éléments glissent vers le haut en apparaissant */
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    /* Animation de pulsation (pour effets d'attention) */
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }

    /* Animation "shimmer" pour les squelettes de chargement (effet brillant) */
    @keyframes shimmer {
      0% { background-position: -1000px 0; }
      100% { background-position: 1000px 0; }
    }

    /* Conteneur principal du panier : apparaît avec l'animation fadeIn */
    .cart-container { animation: fadeIn 0.5s ease-out; }

    /* Chaque ligne de produit : apparaît en douceur et réagit au survol */
    .cart-item { animation: fadeIn 0.5s ease-out; transition: all 0.3s ease; }

    /* Bouton "Procéder à la validation" : effet de survol avec légère élévation */
    .btn-checkout { transition: all 0.3s ease; }
    .btn-checkout:hover { transform: translateY(-2px); box-shadow: 0 5px 15px rgba(59, 89, 152, 0.3); }

    /* Boutons +/- pour la quantité : changent de couleur au survol */
    .quantity-btn { transition: all 0.2s ease; }
    .quantity-btn:hover:not(:disabled) { background-color: #e9ecef; }

    /* Squelette de chargement : barre grise animée qui simule le contenu */
    .loading-skeleton {
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 1000px 100%;
      animation: shimmer 2s infinite;
      border-radius: 4px;
    }

    /* Fond général de la page panier */
    .page-content { background-color: #F8FAFD; padding: 30px 0; }

    /* En-tête du panier : débordement caché pour les effets CSS */
    .cart-header { position: relative; overflow: hidden; }

    /* Ligne de produit : ligne décorative bleu en bas qui apparaît au survol */
    .cart-item { position: relative; overflow: hidden; }
    .cart-item::after {
      content: '';
      position: absolute;
      bottom: 0; left: 0;
      width: 100%; height: 3px;
      background: linear-gradient(90deg, rgba(59, 89, 152, 0) 0%, rgba(59, 89, 152, 0.3) 50%, rgba(59, 89, 152, 0) 100%);
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    .cart-item:hover::after { opacity: 1; }

    /* Conteneur de l'image produit : coins arrondis et ombre légère */
    .product-image-container { position: relative; overflow: hidden; border-radius: 6px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); }

    /* Image produit : léger zoom au survol */
    .product-image { transition: transform 0.3s ease; }
    .product-image:hover { transform: scale(1.05); }

    /* Contrôles de quantité : bordure dégradée subtile autour */
    .quantity-controls { position: relative; }
    .quantity-controls::before {
      content: '';
      position: absolute;
      top: -2px; left: -2px; right: -2px; bottom: -2px;
      border-radius: 6px;
      background: linear-gradient(135deg, #3B5998, #5B7EC2);
      z-index: -1;
      opacity: 0.1;
    }

    /* Carte résumé : cercle décoratif bleu en haut à droite */
    .summary-card { position: relative; overflow: hidden; }
    .summary-card::before {
      content: '';
      position: absolute;
      top: 0; right: 0;
      width: 150px; height: 150px;
      background: radial-gradient(circle, rgba(59, 89, 152, 0.1) 0%, rgba(59, 89, 152, 0) 70%);
      z-index: 0;
    }

    /* Barre de sélection : transition fluide lors de la sélection de produits */
    .selection-bar { transition: all 0.3s ease; }

    /* Checkbox personnalisée : couleur bleue de la marque */
    .checkbox-item { cursor: pointer; accent-color: #3B5998; }
  `
document.head.appendChild(styleSheet)


/* ============================================================================
 *  COMPOSANT PRINCIPAL : Cart
 * ============================================================================ */
const Cart = ({ onSuccess, param = {} }) => {

    // ── Contexte & configuration ─────────────────────────────────────────────
    const { theme } = useTheme()

    // Récupération des données stockées dans le navigateur (localStorage)
    // Ces données sont définies lors de la connexion de l'utilisateur
    const mode = JSON.parse(localStorage.getItem("appMode"))               // Modes API (ex: "listCommandeproduct", "getClientPanier", etc.)
    const apiEndpointe = JSON.parse(localStorage.getItem("apiEndpointe"))  // URLs des endpoints API
    const userData = JSON.parse(localStorage.getItem("userData"))           // Données de l'utilisateur connecté (ID, token, etc.)


    // ── États (state) du composant ───────────────────────────────────────────

    const [user, setUser] = useState(null)                   // Utilisateur courant
    const [productData, setProductData] = useState([])       // Liste des produits dans le panier (depuis l'ERP / 8sens)
    const [quantities, setQuantities] = useState({})         // Quantités locales { PlvID: quantité } — permet la saisie libre
    const [statusCode, setStatusCode] = useState(null)       // Code HTTP de la dernière réponse API
    const [isLoading, setIsLoading] = useState(false)        // Vrai pendant le chargement initial des données
    const [cart, setCart] = useState([])                    // Données du résumé panier (montant HT, TTC, TVA, etc.) 
    const [selectedItems, setSelectedItems] = useState([])   // IDs des produits cochés (sélection multiple)
    const [updatingProductId, setUpdatingProductId] = useState(null) // ID du produit en cours de mise à jour (affiche un loader)


    // ── Pagination ───────────────────────────────────────────────────────────
    // On affiche les produits par pages de 4 pour ne pas surcharger l'écran

    const [currentPage, setCurrentPage] = useState(1)        // Numéro de la page actuelle (commence à 1)
    const itemPerPage = 4                                    // Nombre de produits affichés par page
    const [currentData, setCurrentData] = useState([])       // Sous-ensemble des produits à afficher sur la page courante

    // Calcul des indices de début et fin pour découper le tableau productData
    const indexOfLastItem = currentPage * itemPerPage         // Ex: page 2 → index 8 (on affiche les éléments 4 à 7)
    const indexOfFirstItem = indexOfLastItem - itemPerPage     // Ex: page 2 → index 4

    /**
     * Mise à jour automatique des données affichées à chaque changement de page
     * ou lorsque la liste des produits change (après ajout/suppression).
     */
    useEffect(() => {
        setCurrentData(productData.slice(indexOfFirstItem, indexOfLastItem))
        console.log('productData:', productData)
    }, [currentPage, productData])

    /** Passer à la page suivante (si on n'est pas déjà sur la dernière) */
    const handleNextPage = () => {
        if (indexOfLastItem < productData.length) {
            setCurrentPage((prev) => prev + 1)
        }
    }

    /** Revenir à la page précédente (si on n'est pas déjà sur la première) */
    const handlePreviousPage = () => {
        if (indexOfFirstItem > 0) {
            setCurrentPage((prev) => prev - 1)
        }
    }


    // ══════════════════════════════════════════════════════════════════════════
    //  FONCTIONS API — Communication avec le serveur backend
    // ══════════════════════════════════════════════════════════════════════════

    /**
     * Récupère la liste des produits du panier depuis l'API ERP (8sens).
     * C'est la colonne de gauche : les lignes de produits avec leurs détails.
     *
     * @param {Object} params  - Paramètres de la requête (mode, LG_AGEID, etc.)
     * @param {string} url     - URL de l'endpoint API
     * @param {Function} setter - Fonction setState pour stocker les données reçues
     */
    const fetchData = (params, url, setter) => {
        setIsLoading(true)
        crudData(params, url)
            .then((response) => {
                if (response && response.status === 200) {
                    const rawLines = response.data.lines

                    // L'API peut retourner "lines" sous forme de tableau OU d'objet
                    // On normalise toujours en tableau pour un traitement uniforme
                    let produitVeto = []
                    if (Array.isArray(rawLines)) {
                        produitVeto = rawLines
                    } else if (rawLines && typeof rawLines === "object") {
                        produitVeto = Object.values(rawLines)
                    }

                    // Stocker les produits dans le state
                    setter(produitVeto)
                    setStatusCode(response.status)

                    // Construire le dictionnaire des quantités initiales
                    // Chaque produit a un identifiant unique (PlvID) et une quantité (PlvQteUV)
                    const initialQuantities = produitVeto.reduce((acc, product) => {
                        const id = product.PlvID || product.lg_cprid || product.PlvCode
                        if (id) acc[id] = product.PlvQteUV || product.int_cprquantity || 1
                        return acc
                    }, {})
                    setQuantities(initialQuantities)

                } else {
                    console.error("Erreur HTTP:", response)
                    setStatusCode(response?.status || "Unknown error")
                }
            })
            .catch((error) => {
                setStatusCode(404)
                console.error("Erreur:", error)
            })
            .finally(() => setIsLoading(false))
    }

    /**
     * Récupère le résumé financier du panier (montant HT, TTC, TVA).
     * C'est la colonne de droite : "Résumé de la commande".
     *
     * @param {Object} params  - Paramètres de la requête
     * @param {string} url     - URL de l'endpoint API
     * @param {Function} setter - Fonction setState pour stocker les données
     */
    const fetchPanierData = (params, url, setter) => {
        crudData(params, url)
            .then((response) => {
                if (response && response.status === 200) {
                    setter(response.data.data || [])
                } else {
                    console.error("Erreur HTTP:", response)
                }
            })
            .catch((error) => console.error("Erreur:", error))
    }

    /**
     * Recharge TOUTES les données du panier (produits + résumé financier).
     * Appelée après chaque modification (ajout, suppression, changement de quantité).
     */
    const reloadCart = () => {
        // 1. Recharger la liste des produits (colonne gauche)
        const params = { mode: mode.listCommandeproductMode, LG_AGEID: userData?.LG_AGEID }
        // const params = { mode: mode.getClientPanierMode, LG_AGEID: userData?.LG_AGEID }
        fetchData(params, apiEndpointe.CommandeManagerEndPoint, setProductData)

        // 2. Recharger le résumé financier (colonne droite)
        const paramPanier = { mode: mode.getClientPanierMode, LG_AGEID: userData?.LG_AGEID }
        fetchPanierData(paramPanier, apiEndpointe.CommandeManagerEndPoint, setCart)
    }

    /**
     * Envoie une requête de mise à jour ou de suppression d'un produit au backend.
     * Après succès :
     *   - Met à jour la jauge (TopBar) via onSuccess()
     *   - Recharge tout le panier via reloadCart()
     *
     * @param {Object} params     - Paramètres de la requête (mode, ID produit, quantité, token)
     * @param {string} url        - URL de l'endpoint
     * @param {Function} setter   - Fonction setState (non utilisée ici mais présente par convention)
     * @param {Function} onComplete - Callback appelé à la fin (succès ou erreur), pour retirer le loader
     */
    const DeletUpdateCommproduit = (params, url, setter, onComplete) => {
        crudData(params, url)
            .then((response) => {
                if (response.status === 200) {
                    if (response.data.code_statut === "1") {
                        // Succès → Rafraîchir la jauge et le panier
                        onSuccess()
                        reloadCart()
                    } else {
                        // Erreur métier retournée par l'API → afficher un toast
                        toast.error("Erreur : " + response.data.desc_statut)
                    }
                }
            })
            .catch((error) => console.error("Erreur:", error))
            .finally(() => {
                // Retirer le loader du produit (que ce soit un succès ou un échec)
                if (onComplete) onComplete();
            })
    }


    // ── Chargement initial ───────────────────────────────────────────────────
    // Au montage du composant, on charge les données utilisateur et le panier
    useEffect(() => {
        setUser(userData)
        reloadCart()
    }, [])


    // ══════════════════════════════════════════════════════════════════════════
    //  GESTION DES QUANTITÉS
    // ══════════════════════════════════════════════════════════════════════════

    /**
     * Modifie la quantité d'un produit dans le panier.
     * Appelée par les boutons +/- ou lors de la validation de la saisie manuelle.
     *
     * @param {string} productId - Identifiant unique du produit (PlvID)
     * @param {number} change    - Valeur à ajouter (ex: +1 ou -1, ou la différence pour la saisie manuelle)
     */
    const handleQuantityChange = (productId, change) => {
        // Afficher le loader sur ce produit pendant la mise à jour
        setUpdatingProductId(productId);

        // Récupérer la quantité actuelle (soit depuis notre state local, soit depuis les données ERP)
        const currentQuantity = quantities[productId] !== undefined
            ? quantities[productId]
            : productData.find((p) => p.PlvID === productId).PlvQteUV;

        // Calculer la nouvelle quantité (minimum 1, on ne peut pas avoir 0 produit)
        const newQuantity = Math.max(1, parseInt(currentQuantity) + change);

        // Mettre à jour l'affichage immédiatement (optimistic update)
        setQuantities(prev => ({ ...prev, [productId]: newQuantity }));

        // Envoyer la modification au serveur
        const params = {
            mode: mode.updateCommproduitMode,
            LG_CPRID: productId,
            INT_CPRQUANTITY: newQuantity,
            STR_UTITOKEN: userData.STR_UTITOKEN,
        };

        DeletUpdateCommproduit(params, apiEndpointe.CommandeManagerEndPoint, setProductData, () => setUpdatingProductId(null));
    }


    // ══════════════════════════════════════════════════════════════════════════
    //  SÉLECTION MULTIPLE & SUPPRESSION
    // ══════════════════════════════════════════════════════════════════════════

    /**
     * Coche ou décoche un produit individuel.
     * Si le produit est déjà sélectionné, on le retire de la sélection, sinon on l'ajoute.
     */
    const handleSelectItem = (productId) => {
        setSelectedItems((prev) =>
            prev.includes(productId)
                ? prev.filter((id) => id !== productId)   // Retirer de la sélection
                : [...prev, productId]                     // Ajouter à la sélection
        )
    }

    /**
     * Coche ou décoche TOUS les produits d'un coup.
     * Si tout est déjà sélectionné → tout décocher.
     * Sinon → tout cocher.
     */
    const handleSelectAll = () => {
        if (selectedItems.length === productData.length) {
            setSelectedItems([])                              // Tout décocher
        } else {
            setSelectedItems(productData.map((p) => p.PlvID)) // Tout cocher
        }
    }

    /**
     * Supprime tous les produits sélectionnés (cochés) du panier.
     *
     * STRATÉGIE : "Optimistic update"
     *   1. On retire immédiatement les produits de l'affichage (sans attendre l'API)
     *   2. On envoie les requêtes de suppression en parallèle au serveur
     *   3. Si ça échoue, on recharge tout pour rétablir l'état correct
     */
    const handleDeleteSelected = () => {
        if (selectedItems.length === 0) return

        const itemsToDelete = [...selectedItems]

        // 1. Mise à jour immédiate de l'affichage (retirer les produits supprimés)
        const newData = productData.filter(p => !itemsToDelete.includes(p.PlvID))
        setProductData(newData)
        setSelectedItems([])
        setCurrentPage(1) // Revenir à la page 1 pour éviter une page vide

        // 2. Mettre à jour le compteur de la jauge immédiatement
        onSuccess(newData.length)

        // 3. Envoyer toutes les requêtes de suppression en parallèle
        const deletePromises = itemsToDelete.map((productId) => {
            const params = {
                mode: mode.deleteCommproduitMode,
                LG_CPRID: productId,
                LG_AGEID: userData.LG_AGEID,
                STR_UTITOKEN: userData.STR_UTITOKEN,
            }
            return crudData(params, apiEndpointe.CommandeManagerEndPoint)
        })

        // 4. Attendre que toutes les suppressions soient terminées
        Promise.all(deletePromises)
            .then(() => {
                toast.success(`${itemsToDelete.length} produit(s) supprimé(s)`)
                onSuccess(0)    // Rafraîchir la jauge
                reloadCart()    // Recharger le panier complet depuis le serveur
            })
            .catch(() => {
                toast.error("Erreur lors de la suppression.")
                reloadCart()    // En cas d'erreur → recharger pour rétablir l'état correct
            })
    }


    // ══════════════════════════════════════════════════════════════════════════
    //  NAVIGATION VERS UN PRODUIT
    // ══════════════════════════════════════════════════════════════════════════

    /**
     * Quand on clique sur un produit dans le panier pour voir ses détails,
     * on sauvegarde son ID dans le localStorage pour que la page DetailProduct
     * sache quel produit afficher.
     */
    const handleClick = (product) => {
        const selectedProduct = cart.produits?.find((p) => p.lg_cprid === product.PlvID)
        if (selectedProduct) {
            localStorage.setItem("selectedProductId", selectedProduct.lg_proid)
        }
    }


    // ══════════════════════════════════════════════════════════════════════════
    //  RENDU JSX — Tout l'affichage visuel du panier
    // ══════════════════════════════════════════════════════════════════════════

    return (
        <div className={`${param.userData ? "bgUserConnected" : ""}`}>
            <div className="page-wrapper">
                <main className="main cart">

                    {/* Menu de navigation latéral (catégories, etc.) */}
                    <AppMenu />

                    {/* ═══════════════════════════════════════════════════════
                         EN-TÊTE DU PANIER — Titre + compteur d'articles
                    ═══════════════════════════════════════════════════════ */}
                    <div className="container">
                        <div
                            className="cart-header"
                            style={{
                                padding: "20px 0px",
                                borderRadius: "10px 10px 0 0",
                                marginBottom: "0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                position: "relative",
                                borderBottom: "1px solid rgba(59, 89, 152, 0.1)",
                            }}
                        >
                            {/* Titre principal */}
                            <h1 style={{ color: "#3B5998", fontSize: "24px", fontWeight: "700", margin: "0", letterSpacing: "0.5px", position: "relative", zIndex: "1" }}>
                                MON PANIER
                            </h1>

                            {/* Badge compteur : nombre d'articles dans le panier */}
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", position: "relative", zIndex: "1" }}>
                                <div style={{ backgroundColor: "#3B5998", color: "#FFFFFF", padding: "6px 12px", borderRadius: "20px", fontSize: "14px", fontWeight: "600", display: "flex", alignItems: "center", gap: "5px" }}>
                                    <i className="fas fa-shopping-cart" style={{ fontSize: "12px" }}></i>
                                    <span>{productData.length} article(s)</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ═══════════════════════════════════════════════════════
                         CONTENU PRINCIPAL — Grille : Produits (8/12) + Résumé (4/12)
                    ═══════════════════════════════════════════════════════ */}
                    <div className="page-content" style={{ paddingTop: "0" }}>
                        <div className="container">
                            <div
                                className="row gutter-lg page-contnainer cart-container"
                                style={{
                                    minHeight: "314px",
                                    //backgroundColor: "#FFFFFF",
                                    borderRadius: "0 0 8px 8px",
                                    //boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                                    padding: "0",
                                    overflow: "hidden",
                                    border: "1px solid #E8EFF9",
                                    borderTop: "none",
                                }}
                            >
                                {/* Vérifier si le panier contient des produits */}
                                {productData || productData.length > 0 ? (
                                    <>
                                        {/* ─────────────────────────────────────────────
                                             COLONNE GAUCHE — Liste des produits (8/12)
                                        ───────────────────────────────────────────── */}
                                        <div className="col-lg-8 pr-lg-4 mb-6 table-min-h d-flex flex-column" style={{ padding: "20px" }}>

                                            {/* ── En-tête du tableau (titres des colonnes) ── */}
                                            <div
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "40px 100px 2fr 1fr 1fr 1fr",
                                                    gap: "15px",
                                                    padding: "10px 15px",
                                                    backgroundColor: "#F5F8FC",
                                                    borderRadius: "8px",
                                                    marginBottom: "10px",
                                                    fontWeight: "600",
                                                    color: "#3B5998",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {/* Checkbox "tout sélectionner" */}
                                                <div>
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox-item"
                                                        checked={selectedItems.length === productData.length && productData.length > 0}
                                                        onChange={handleSelectAll}
                                                        style={{ width: "18px", height: "18px" }}
                                                    />
                                                </div>
                                                <div>Produit</div>
                                                <div>Description</div>
                                                <div>Prix</div>
                                                <div>Quantité</div>
                                                <div>Total</div>
                                            </div>

                                            {/* ── Barre de sélection (affiche le nombre de produits cochés + bouton supprimer) ── */}
                                            <div
                                                className="selection-bar"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    padding: "10px 15px",
                                                    backgroundColor: selectedItems.length > 0 ? "#FFF3F3" : "#F5F8FC",
                                                    borderRadius: "8px",
                                                    marginBottom: "15px",
                                                    border: selectedItems.length > 0 ? "1px solid #FF5252" : "1px solid #E8EFF9",
                                                }}
                                            >
                                                {/* Texte indiquant combien de produits sont sélectionnés */}
                                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                    <span style={{ color: "#3B5998", fontWeight: "600", fontSize: "14px" }}>
                                                        {selectedItems.length > 0
                                                            ? `${selectedItems.length} produit(s) sélectionné(s)`
                                                            : "Aucun produit sélectionné"
                                                        }
                                                    </span>
                                                </div>

                                                {/* Bouton "Supprimer" — visible uniquement si au moins 1 produit est coché */}
                                                {selectedItems.length > 0 && (
                                                    <button
                                                        onClick={handleDeleteSelected}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "8px",
                                                            padding: "8px 16px",
                                                            background: "linear-gradient(135deg, #FF5252, #FF7676)",
                                                            color: "#FFFFFF",
                                                            border: "none",
                                                            borderRadius: "6px",
                                                            cursor: "pointer",
                                                            fontWeight: "600",
                                                            fontSize: "14px",
                                                            boxShadow: "0 4px 10px rgba(255, 82, 82, 0.3)",
                                                        }}
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                        Supprimer ({selectedItems.length})
                                                    </button>
                                                )}
                                            </div>

                                            {/* ── Liste des articles du panier ── */}
                                            <div style={{ display: "flex", flexDirection: "column", gap: "15px", marginBottom: "20px" }}>

                                                {/* CAS 1 : Chargement en cours → Afficher des squelettes animés */}
                                                {isLoading && currentData.length === 0 ? (
                                                    Array(3).fill().map((_, index) => (
                                                        <div
                                                            key={index}
                                                            style={{
                                                                display: "grid",
                                                                gridTemplateColumns: "40px 100px 2fr 1fr 1fr 1fr",
                                                                gap: "15px",
                                                                padding: "15px",
                                                                backgroundColor: "#FFFFFF",
                                                                borderRadius: "8px",
                                                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                                border: "1px solid #E8EFF9",
                                                            }}
                                                        >
                                                            <div className="loading-skeleton" style={{ height: "18px", width: "18px" }}></div>
                                                            <div className="loading-skeleton" style={{ height: "80px", width: "80px" }}></div>
                                                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                                                <div className="loading-skeleton" style={{ height: "20px", width: "80%" }}></div>
                                                                <div className="loading-skeleton" style={{ height: "16px", width: "50%" }}></div>
                                                            </div>
                                                            <div className="loading-skeleton" style={{ height: "20px", width: "70%" }}></div>
                                                            <div className="loading-skeleton" style={{ height: "36px", width: "100%" }}></div>
                                                            <div className="loading-skeleton" style={{ height: "20px", width: "70%" }}></div>
                                                        </div>
                                                    ))

                                                    /* CAS 2 : Chargement terminé mais panier vide → Message d'invitation */
                                                ) : !isLoading && currentData.length === 0 ? (
                                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0", backgroundColor: "#F5F8FC", borderRadius: "10px", border: "1px dashed #3B5998" }}>
                                                        <div style={{ width: "80px", height: "80px", borderRadius: "50%", backgroundColor: "rgba(59, 89, 152, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
                                                            <i className="fas fa-shopping-cart" style={{ fontSize: "36px", color: "#3B5998" }}></i>
                                                        </div>
                                                        <p style={{ fontSize: "20px", color: "#3B5998", fontWeight: "600", marginBottom: "10px" }}>Votre panier est vide</p>
                                                        <p style={{ fontSize: "14px", color: "#6C757D", marginBottom: "25px", textAlign: "center", maxWidth: "300px" }}>Parcourez nos catégories et découvrez nos meilleures offres!</p>
                                                        <NavLink to="/catalogue" style={{ background: "linear-gradient(135deg, #3B5998, #5B7EC2)", color: "#FFFFFF", padding: "12px 25px", borderRadius: "6px", textDecoration: "none", fontWeight: "600", boxShadow: "0 4px 10px rgba(59, 89, 152, 0.2)" }}>
                                                            Commencez vos achats
                                                        </NavLink>
                                                    </div>

                                                    /* CAS 3 : Panier avec des produits → Afficher chaque ligne */
                                                ) : (
                                                    currentData && currentData.map((product, index) => (
                                                        <div
                                                            key={index}
                                                            className="cart-item"
                                                            style={{
                                                                position: "relative",
                                                                display: "grid",
                                                                gridTemplateColumns: "40px 100px 2fr 1fr 1fr 1fr",
                                                                gap: "15px",
                                                                padding: "20px",
                                                                backgroundColor: selectedItems.includes(product.PlvID) ? "#F0F4FF" : "#FFFFFF",
                                                                borderRadius: "10px",
                                                                //boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                                                alignItems: "center",
                                                                border: selectedItems.includes(product.PlvID) ? "1px solid #3B5998" : "1px solid #E8EFF9",
                                                                transition: "all 0.2s ease",
                                                            }}
                                                        >
                                                            {/* Overlay de chargement — affiché quand ce produit est en cours de mise à jour */}
                                                            {updatingProductId === product.PlvID && (
                                                                <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(255,255,255,0.8)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 10, borderRadius: "10px", backdropFilter: "blur(2px)" }}>
                                                                    <i className="fas fa-spinner fa-spin" style={{ color: "#3B5998", fontSize: "40px", marginBottom: "10px" }}></i>
                                                                    <span style={{ color: "#3B5998", fontWeight: "600", fontSize: "16px" }}>Mise à jour en cours...</span>
                                                                </div>
                                                            )}

                                                            {/* Colonne 1 : Checkbox de sélection */}
                                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                <input
                                                                    type="checkbox"
                                                                    className="checkbox-item"
                                                                    checked={selectedItems.includes(product.PlvID)}
                                                                    onChange={() => handleSelectItem(product.PlvID)}
                                                                    style={{ width: "18px", height: "18px" }}
                                                                />
                                                            </div>

                                                            {/* Colonne 2 : Image du produit (cliquable → page détail) */}
                                                            <div className="product-image-container" style={{ position: "relative", width: "100px", height: "100px" }}>
                                                                <NavLink to={`/${product.str_proslug}`} onClick={() => handleClick(product)}>
                                                                    <img
                                                                        src={`${product.str_propic ? fullUrl + product.str_propic : fullUrl + defaultImage}`}
                                                                        alt={product.PlvLib}
                                                                        className="product-image"
                                                                        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px", border: "1px solid #E8EFF9" }}
                                                                    />
                                                                </NavLink>
                                                            </div>

                                                            {/* Colonne 3 : Nom du produit (1 ligne + "...") + code article */}
                                                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", overflow: "hidden" }}>
                                                                <NavLink
                                                                    to={`/${product.str_proslug}`}
                                                                    onClick={() => handleClick(product)}
                                                                    style={{ fontSize: "16px", fontWeight: "600", color: "#3B5998", textDecoration: "none", lineHeight: "1.4", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}
                                                                    onMouseOver={(e) => { e.target.style.color = "#5B7EC2" }}
                                                                    onMouseOut={(e) => { e.target.style.color = "#3B5998" }}
                                                                    title={product.PlvLib}
                                                                >
                                                                    {product.PlvLib}
                                                                </NavLink>
                                                                <span style={{ fontSize: "14px", color: "#6C757D", padding: "2px 8px", backgroundColor: "#F8FAFD", borderRadius: "4px", display: "inline-block", width: "fit-content" }}>
                                                                    {product.PlvCode}
                                                                </span>
                                                            </div>

                                                            {/* Colonne 4 : Prix unitaire (1 ligne + "...") */}

                                                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", overflow: "hidden" }}>
                                                                <span style={{
                                                                    fontSize: "13px", color: "#6C757D", fontWeight: "500",
                                                                    textTransform: "uppercase", letterSpacing: "0.5px"
                                                                }}>
                                                                    Prix unitaire
                                                                </span>

                                                                {(() => {
                                                                    // Prix négocié ERP (depuis listCommandeproduct)
                                                                    const prixNegocie = parseFloat(product.PlvPUNet) || 0

                                                                    // Prix catalogue BD (depuis getClientPanier == cart.produits)
                                                                    const produitBD = cart?.produits?.find(p => p.lg_cprid === product.PlvID)
                                                                    const prixCatalogue = parseFloat(produitBD?.PlvPUNet) || 0

                                                                    console.log("produitBD:", produitBD)
                                                                    console.log("prixCatalogue:", prixCatalogue)
                                                                    console.log("prixNegocie:", prixNegocie)

                                                                    const hasReduction = prixCatalogue > 0 && prixCatalogue > prixNegocie

                                                                    return hasReduction ? (
                                                                        <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                                                                            {/* Prix catalogue barré */}
                                                                            <span style={{
                                                                                textDecoration: "line-through",
                                                                                color: "#999", fontSize: "13px",
                                                                            }}>
                                                                                {/* {formatPrice(parseInt(prixCatalogue))} FCFA */}
                                                                                {formatPrice(Math.round(prixCatalogue))} FCFA
                                                                            </span>
                                                                            {/* Prix négocié */}
                                                                            <span style={{
                                                                                fontSize: "16px", fontWeight: "600", color: "#e74c3c",
                                                                                whiteSpace: "nowrap"
                                                                            }}>
                                                                                {formatPrice(Math.round(prixNegocie))} <sup style={{ fontSize: "10px" }}>FCFA</sup>
                                                                                {/* {formatPrice(parseInt(prixNegocie))} <sup style={{ fontSize: "10px" }}>FCFA</sup> */}
                                                                            </span>
                                                                            {/* Badge % */}
                                                                            <span style={{
                                                                                background: "#e74c3c", color: "white",
                                                                                fontSize: "10px", fontWeight: "700",
                                                                                padding: "1px 6px", borderRadius: "10px",
                                                                                width: "fit-content",
                                                                            }}>
                                                                                -{Math.round(((prixCatalogue - prixNegocie) / prixCatalogue) * 100)}%
                                                                            </span>
                                                                        </div>
                                                                    ) : (
                                                                        <span style={{
                                                                            fontSize: "16px", fontWeight: "600", color: "#3B5998",
                                                                            background: "linear-gradient(135deg, #3B5998, #5B7EC2)",
                                                                            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                                                                            whiteSpace: "nowrap"
                                                                        }}>
                                                                            {formatPrice(parseInt(prixNegocie))} <sup style={{ fontSize: "10px", fontWeight: "500" }}>FCFA</sup>
                                                                        </span>
                                                                    )
                                                                })()}
                                                            </div>
                                                
                                                            {/* <div style={{ display: "flex", flexDirection: "column", gap: "8px", overflow: "hidden" }}>
                                                                <span style={{ fontSize: "13px", color: "#6C757D", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Prix unitaire</span>
                                                                <span style={{ fontSize: "16px", fontWeight: "600", color: "#3B5998", background: "linear-gradient(135deg, #3B5998, #5B7EC2)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", whiteSpace: "nowrap" }}>
                                                                    {product && formatPrice(parseInt(product.PlvPUNet))} <sup style={{ fontSize: "10px", fontWeight: "500" }}>FCFA</sup>
                                                                </span>
                                                            </div> */}

                                                            {/* Colonne 5 : Contrôles de quantité (bouton − / champ / bouton +) */}
                                                            <div>
                                                                <div className="quantity-controls" style={{ display: "flex", alignItems: "center", border: "1px solid #E8EFF9", borderRadius: "6px", overflow: "hidden", width: "auto", height: "40px", backgroundColor: "#FFFFFF", position: "relative" }}>

                                                                    {/* Bouton "−" : diminuer la quantité de 1 */}
                                                                    <button
                                                                        className="quantity-btn"
                                                                        onClick={() => handleQuantityChange(product.PlvID, -1)}
                                                                        disabled={quantities[product.PlvID] <= 1 || updatingProductId === product.PlvID}
                                                                        style={{ width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: quantities[product.PlvID] <= 1 ? "#F5F8FC" : "#E8EFF9", color: quantities[product.PlvID] <= 1 ? "#ADB5BD" : "#3B5998", border: "none", cursor: quantities[product.PlvID] <= 1 || updatingProductId === product.PlvID ? "not-allowed" : "pointer", fontSize: "16px" }}
                                                                    >
                                                                        <i className="w-icon-minus"></i>
                                                                    </button>

                                                                    {/* Champ de saisie de la quantité
                                                                         - On peut effacer le champ et taper une nouvelle valeur
                                                                         - La mise à jour est envoyée au serveur uniquement quand :
                                                                           • on quitte le champ (onBlur)
                                                                           • on appuie sur Entrée
                                                                         - Si le champ est laissé vide → on remet la quantité d'origine
                                                                    */}
                                                                    <input
                                                                        type="number"
                                                                        min={1}
                                                                        value={quantities[product.PlvID] !== undefined ? quantities[product.PlvID] : product.PlvQteUV}
                                                                        disabled={updatingProductId === product.PlvID}
                                                                        onChange={(e) => {
                                                                            const raw = e.target.value;
                                                                            // Autoriser le champ vide pendant la saisie
                                                                            if (raw === "" || raw === null) {
                                                                                setQuantities(prev => ({ ...prev, [product.PlvID]: "" }));
                                                                                return;
                                                                            }
                                                                            const val = parseInt(raw);
                                                                            if (!isNaN(val) && val >= 0) {
                                                                                setQuantities(prev => ({ ...prev, [product.PlvID]: val }));
                                                                            }
                                                                        }}
                                                                        onBlur={() => {
                                                                            const current = quantities[product.PlvID];
                                                                            const original = parseInt(product.PlvQteUV) || 1;
                                                                            // Si vide ou invalide → remettre la quantité d'origine
                                                                            if (current === "" || current === undefined || current === null || isNaN(current) || current < 1) {
                                                                                setQuantities(prev => ({ ...prev, [product.PlvID]: original }));
                                                                                return;
                                                                            }
                                                                            // Envoyer la mise à jour seulement si la quantité a changé
                                                                            if (parseInt(current) !== original) {
                                                                                handleQuantityChange(product.PlvID, parseInt(current) - (parseInt(quantities[product.PlvID]) || original));
                                                                            }
                                                                        }}
                                                                        onKeyDown={(e) => {
                                                                            // Appuyer sur Entrée = quitter le champ  déclenche le onBlur
                                                                            if (e.key === "Enter") {
                                                                                e.target.blur();
                                                                            }
                                                                        }}
                                                                        max={100000}
                                                                        style={{ width: "70px", height: "40px", textAlign: "center", border: "none", borderLeft: "1px solid #E8EFF9", borderRight: "1px solid #E8EFF9", color: "#3B5998", fontWeight: "600", fontSize: "16px" }}
                                                                    />

                                                                    {/* Bouton "+" : augmenter la quantité de 1 */}
                                                                    <button
                                                                        className="quantity-btn"
                                                                        onClick={() => handleQuantityChange(product.PlvID, 1)}
                                                                        disabled={updatingProductId === product.PlvID}
                                                                        style={{ width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#E8EFF9", color: "#3B5998", border: "none", cursor: updatingProductId === product.PlvID ? "not-allowed" : "pointer", fontSize: "16px" }}
                                                                    >
                                                                        <i className="w-icon-plus"></i>
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {/* Colonne 6 : Total de la ligne (prix unitaire × quantité) */}
                                                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end" }}>
                                                                <span style={{ fontSize: "13px", color: "#6C757D", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total</span>
                                                                <span style={{ fontSize: "18px", fontWeight: "700", color: "#3B5998", background: "linear-gradient(135deg, #3B5998, #5B7EC2)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                                                                    {product && Math.round(Number(product.PlvQteUV) * Number(product.PlvPUNet))} <sup style={{ fontSize: "10px", fontWeight: "500" }}>FCFA</sup>
                                                                    {/* {product && formatPrice(parseInt(product.PlvQteUV) * parseInt(product.PlvPUNet))} <sup style={{ fontSize: "10px", fontWeight: "500" }}>FCFA</sup> */}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>

                                            {/* ── Boutons de pagination (Précédent / Suivant) ── */}
                                            {productData && productData.length > 0 && (
                                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "25px", gap: "12px" }}>
                                                    <button
                                                        onClick={handlePreviousPage}
                                                        disabled={indexOfFirstItem <= 0}
                                                        style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 18px", background: indexOfFirstItem <= 0 ? "#F5F8FC" : "linear-gradient(135deg, #3B5998, #5B7EC2)", color: indexOfFirstItem <= 0 ? "#ADB5BD" : "#FFFFFF", border: "none", borderRadius: "6px", cursor: indexOfFirstItem <= 0 ? "not-allowed" : "pointer", fontWeight: "600", boxShadow: indexOfFirstItem <= 0 ? "none" : "0 4px 10px rgba(59, 89, 152, 0.2)" }}
                                                    >
                                                        <i className="w-icon-long-arrow-left"></i> Précédent
                                                    </button>
                                                    <button
                                                        onClick={handleNextPage}
                                                        disabled={indexOfLastItem >= productData.length}
                                                        style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 18px", background: indexOfLastItem >= productData.length ? "#F5F8FC" : "linear-gradient(135deg, #3B5998, #5B7EC2)", color: indexOfLastItem >= productData.length ? "#ADB5BD" : "#FFFFFF", border: "none", borderRadius: "6px", cursor: indexOfLastItem >= productData.length ? "not-allowed" : "pointer", fontWeight: "600", boxShadow: indexOfLastItem >= productData.length ? "none" : "0 4px 10px rgba(59, 89, 152, 0.2)" }}
                                                    >
                                                        Suivant <i className="w-icon-long-arrow-right"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* ─────────────────────────────────────────────
                                             COLONNE DROITE — Résumé de la commande (4/12)
                                             Affichée seulement si le panier contient des produits
                                        ───────────────────────────────────────────── */}
                                        {cart && Object.entries(cart).length > 0 && productData.length > 0 && (
                                            <div className="col-lg-4 sticky-sidebar-wrapper" style={{ padding: "20px", borderLeft: "1px solid #E8EFF9" }}>
                                                <div className="sticky-sidebar">
                                                    <div className="summary-card" style={{ backgroundColor: "#F5F8FC", borderRadius: "10px", padding: "25px", border: "1px solid #E8EFF9", boxShadow: "0 4px 15px rgba(59, 89, 152, 0.08)", position: "relative" }}>

                                                        {/* Titre du bloc résumé */}
                                                        <h3 style={{ color: "#3B5998", fontSize: "20px", fontWeight: "700", marginBottom: "25px", textAlign: "center", textTransform: "uppercase", letterSpacing: "1px", position: "relative" }}>
                                                            Résumé de la commande
                                                            <div style={{ height: "3px", width: "60px", background: "linear-gradient(90deg, #3B5998, #5B7EC2)", margin: "10px auto 0", borderRadius: "3px" }}></div>
                                                        </h3>

                                                        {/* Pendant le chargement ou la mise à jour → spinner */}
                                                        {isLoading || updatingProductId !== null ? (
                                                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "30px 0" }}>
                                                                <i className="fas fa-spinner fa-spin" style={{ color: "#3B5998", fontSize: "40px", marginBottom: "10px" }}></i>
                                                                <span style={{ color: "#3B5998", fontWeight: "600", fontSize: "16px" }}>Calcul en cours...</span>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                {/* Détails financiers */}
                                                                <div style={{ backgroundColor: "#FFFFFF", borderRadius: "8px", padding: "20px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}>

                                                                    {/* Ligne 1 : Montant Hors Taxes */}
                                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", padding: "0 0 15px", borderBottom: "1px solid #E8EFF9" }}>
                                                                        <span style={{ color: "#6C757D", fontSize: "14px", fontWeight: "500" }}>Montant HT</span>
                                                                        <span style={{ color: "#3B5998", fontSize: "16px", fontWeight: "600" }}>{cart && formatPrice(cart?.dbl_commmtht)} <sup style={{ fontSize: "10px", fontWeight: "500" }}>FCFA</sup></span>
                                                                    </div>

                                                                    {/* Ligne 2 : TVA (affichée uniquement si la TVA >= 0, calculée côté backend) */}
                                                                    {cart && (cart.dbl_commmttva >= 0) && (
                                                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", padding: "0 0 15px", borderBottom: "1px solid #E8EFF9" }}>
                                                                            <span style={{ color: "#6C757D", fontSize: "14px", fontWeight: "500" }}>TVA</span>
                                                                            <span style={{ color: "#3B5998", fontSize: "16px", fontWeight: "600" }}>{formatPrice(cart.dbl_commmttva)} <sup style={{ fontSize: "10px", fontWeight: "500" }}>FCFA</sup></span>
                                                                        </div>
                                                                    )}

                                                                    {/* Ligne 3 : Total TTC (Toutes Taxes Comprises) */}
                                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                        <span style={{ color: "#3B5998", fontSize: "16px", fontWeight: "700" }}>Total</span>
                                                                        <span style={{ fontSize: "20px", fontWeight: "800", background: "linear-gradient(135deg, #3B5998, #5B7EC2)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                                                                            {cart && formatPrice(cart?.dbl_commmtttc)} <sup style={{ fontSize: "11px", fontWeight: "500" }}>FCFA</sup>
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {/* Bouton de validation → redirige vers la page de choix de livraison */}
                                                                <NavLink
                                                                    to={"/choix-livraison"}
                                                                    className="btn-checkout"
                                                                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", background: "linear-gradient(135deg, #3B5998, #5B7EC2)", color: "#FFFFFF", padding: "14px 20px", borderRadius: "6px", textDecoration: "none", fontWeight: "700", fontSize: "16px", width: "100%", textAlign: "center", boxShadow: "0 4px 12px rgba(59, 89, 152, 0.25)" }}
                                                                >
                                                                    <span style={{ position: "relative", zIndex: "1" }}>Procéder à la validation</span>
                                                                    <i className="w-icon-long-arrow-right" style={{ position: "relative", zIndex: "1" }}></i>
                                                                </NavLink>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    /* Cas de secours : si productData est null/undefined */
                                    <p className="text-center" style={{ padding: "40px 0", color: "#3B5998", fontSize: "18px", fontWeight: "500" }}>
                                        Votre panier est vide.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    )
}

export default Cart


/* ============================================================================
 *  UTILITAIRE : Formatage des prix
 * ============================================================================
 *  Transforme un nombre en chaîne lisible avec des espaces comme séparateurs
 *  de milliers. Exemples :
 *    formatPrice(1266)   → "1 266"
 *    formatPrice(15000)  → "15 000"
 *    formatPrice(null)   → "0"
 */
export function formatPrice(price) {
    if (price == null) return "0";
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
}